import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, Tooltip } from '@mui/material';
import { AttachFile as AttachFileIcon } from '@mui/icons-material';
import { PaperPlaneRight } from '@phosphor-icons/react';
import { chatTheme } from '../../theme/chatTheme';
import { useAuth } from '../../context/AuthContext';
import FunctionCallIndicator from './FunctionCallIndicator';
import FileSelectionDialog from './FileSelectionDialog';

const MessageInput = ({ 
  onSend, 
  placeholder = "Type a message...", 
  disabled = false,
  isFunctionCalling = false,
  functionCallName = null,
  activeChatId = null,
  socket = null,
  isConnected = false,
  currentUserId = null,
  activeChat = null,
  onFileSelect = null,
}) => {
  const { user } = useAuth();
  const userType = user?.role === 'supplier' ? 'supplier' : 'couple';
  const [message, setMessage] = useState('');
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const textFieldRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  
  // Check if attachment button should be shown
  // Only suppliers can send files (from their business files)
  // Couples cannot send files for security and storage reasons
  const showAttachmentButton = userType === 'supplier' && 
    activeChat?.type === 'supplier' && 
    activeChat?.business?._id;
  
  // Emit typing events
  useEffect(() => {
    if (!socket || !isConnected || !activeChatId || !currentUserId) {
      return;
    }
    
    const handleTyping = () => {
      if (!isTypingRef.current && message.trim()) {
        isTypingRef.current = true;
        socket.emit('chat-typing', {
          chatId: activeChatId,
          userId: currentUserId
        });
      }
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set timeout to stop typing indicator after 2 seconds of no typing
      typingTimeoutRef.current = setTimeout(() => {
        if (isTypingRef.current) {
          isTypingRef.current = false;
          socket.emit('chat-typing-stop', {
            chatId: activeChatId,
            userId: currentUserId
          });
        }
      }, 2000);
    };
    
    if (message.trim()) {
      handleTyping();
    } else {
      // Clear typing if message is empty
      if (isTypingRef.current) {
        isTypingRef.current = false;
        socket.emit('chat-typing-stop', {
          chatId: activeChatId,
          userId: currentUserId
        });
      }
    }
    
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [message, socket, isConnected, activeChatId, currentUserId]);
  
  const handleSend = () => {
    if (message.trim() && !disabled) {
      // Stop typing indicator
      if (socket && isConnected && activeChatId && currentUserId && isTypingRef.current) {
        isTypingRef.current = false;
        socket.emit('chat-typing-stop', {
          chatId: activeChatId,
          userId: currentUserId
        });
      }
      
      onSend(message.trim());
      setMessage('');
      // Reset text field height - safely check if textarea exists
      if (textFieldRef.current) {
        const textarea = textFieldRef.current.querySelector('textarea');
        if (textarea) {
          textarea.style.height = 'auto';
        }
      }
    }
  };
  
  const handleKeyPress = (e) => {
    // Allow Enter to create new lines
    // Only send on Ctrl+Enter or Cmd+Enter (for Mac)
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
    // Otherwise, Enter will create a new line (default behavior for multiline TextField)
  };
  
  return (
    <Box sx={chatTheme.messageInput.container}>
      {/* Function call indicator */}
      {isFunctionCalling && (
        <FunctionCallIndicator functionName={functionCallName} />
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Attachment button - only for suppliers */}
        {showAttachmentButton && (
          <Tooltip title="Attach file">
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => setFileDialogOpen(true)}
              sx={{
                color: 'text.secondary',
                alignSelf: 'center',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {/* Message input */}
        <TextField
          inputRef={textFieldRef}
          fullWidth
          multiline
          maxRows={4}
          placeholder={placeholder}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={disabled || isFunctionCalling}
          sx={chatTheme.messageInput.input}
          InputProps={{
            sx: {
              padding: '14px 16px',
            },
          }}
        />
        
        {/* Send button */}
        <Tooltip title="Send message">
          <span>
            <IconButton
              onClick={handleSend}
              disabled={!message.trim() || disabled || isFunctionCalling}
              sx={chatTheme.messageInput.sendButton}
            >
              <PaperPlaneRight size={20} weight="fill" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      
      {/* File Selection Dialog - only for suppliers */}
      {showAttachmentButton && (
        <FileSelectionDialog
          open={fileDialogOpen}
          onClose={() => setFileDialogOpen(false)}
          businessId={activeChat?.business?._id}
          onFileSelect={onFileSelect}
        />
      )}
    </Box>
  );
};

export default MessageInput;

